/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { memo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { jsx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import AkQuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { LazyDevToolsDialog } from '@confluence/dev-tools';
import { FeedbackSource, LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector';

import { LazyHelpMenu } from './HelpMenuComponent';

export type HelpWidgetProps = {
	contentId: string;
	openHelpDialog: () => void;
	isNCSEditor?: boolean;
	isTemplateEditor?: boolean;
	isCompanyHub?: boolean;
	isContentCreationPanelOpen?: boolean;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
};

const styles = cssMap({
	root: {
		position: 'absolute',
		bottom: token('space.300'),
		left: token('space.250'),
		zIndex: 400,
	},
});

export const HelpWidget = memo(
	({
		openHelpDialog,
		contentId,
		isNCSEditor,
		isTemplateEditor,
		isCompanyHub,
		setIsOpen,
		isOpen,
	}: HelpWidgetProps) => {
		const { formatMessage } = useIntl();

		const [shouldShowDevTools, setShouldShowDevTools] = useState(false);
		const [shouldShowGlobalFeedbackCollector, setShouldShowGlobalFeedbackCollector] =
			useState(false);

		const handleButtonClick = useCallback(
			(e) => {
				e.stopPropagation();
				setIsOpen?.(!isOpen);
			},
			[isOpen, setIsOpen],
		);

		return (
			<>
				<div css={styles.root} data-testId="help-widget">
					{isOpen && (
						<LazyHelpMenu
							openHelpDialog={openHelpDialog}
							contentId={contentId}
							isNCSEditor={isNCSEditor}
							isTemplateEditor={isTemplateEditor}
							isCompanyHub={isCompanyHub}
							isOpen={isOpen}
							setIsOpen={setIsOpen}
							setShouldShowDevTools={setShouldShowDevTools}
							setShouldShowGlobalFeedbackCollector={setShouldShowGlobalFeedbackCollector}
						/>
					)}
					<AkTooltip
						content={formatMessage({ id: 'help.name' })}
						position="right"
						hideTooltipOnClick
					>
						<IconButton
							icon={AkQuestionCircleIcon}
							shape="circle"
							appearance="subtle"
							label={formatMessage({ id: 'help.name' })}
							testId="help-menu-icon-button"
							onClick={handleButtonClick}
							isSelected={isOpen}
							aria-expanded={isOpen}
							color="color.icon.subtlest"
						/>
					</AkTooltip>
				</div>

				<ModalTransition>
					{shouldShowDevTools && (
						<LazyDevToolsDialog onClose={() => setShouldShowDevTools(false)} />
					)}
				</ModalTransition>
				<ModalTransition>
					{shouldShowGlobalFeedbackCollector && (
						<LazyGlobalFeedbackCollector
							feedbackSource={FeedbackSource.EDITOR}
							onClose={() => setShouldShowGlobalFeedbackCollector(false)}
							onCloseComplete={() => {}}
							contentType="page" // TODO: add contentType
						/>
					)}
				</ModalTransition>
			</>
		);
	},
);
